import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Gallery,
  GalleryItem,
  Icon,
  Label,
} from '@patternfly/react-core';
import { CubesIcon, GlobeIcon, MapMarkerIcon, ExclamationCircleIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useApiFetch } from '../../api';
import type { Site } from '../../types/models';
import type { ApiResponseSiteList } from '../../types/api/ApiResponseSite';

const statusColorMap: Record<string, 'green' | 'blue' | 'orange' | 'grey'> = {
  active: 'green',
  planned: 'blue',
  staged: 'orange',
};

function Overview() {
  const apiFetch = useApiFetch();
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSites = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch('/sites');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ApiResponseSiteList = await response.json();
      if (data.status === 'success') {
        setSites(data.data.sites);
      } else {
        setSites([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching sites');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  if (loading) {
    return <CenteredSpinner />;
  }

  if (error) {
    return (
      <Card>
        <CardBody>
          <EmptyState
            titleText="Failed to load sites"
            icon={ExclamationCircleIcon}
            variant="lg"
            headingLevel="h3"
            status="danger"
          >
            <EmptyStateBody>{error}</EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="primary" onClick={fetchSites}>
                  Retry
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        </CardBody>
      </Card>
    );
  }

  if (sites.length === 0) {
    return (
      <Card>
        <CardBody>
          <EmptyState titleText="No dedicated sites" icon={CubesIcon} variant="lg" headingLevel="h3">
            <EmptyStateBody>You have no dedicated sites assigned to your organization.</EmptyStateBody>
          </EmptyState>
        </CardBody>
      </Card>
    );
  }

  return (
    <Gallery hasGutter minWidths={{ default: '300px' }}>
      {sites.map((site) => (
        <GalleryItem key={site.id}>
          <Card isFullHeight>
            <CardTitle>{site.name}</CardTitle>
            <CardBody>
              {site.status && (
                <div style={{ marginBottom: '8px' }}>
                  <Label color={statusColorMap[site.status] ?? 'grey'}>{site.status}</Label>
                </div>
              )}
              {site.time_zone && (
                <Content component="p" style={{ marginBottom: '4px' }}>
                  <Icon size="sm" style={{ marginRight: '6px' }}>
                    <GlobeIcon />
                  </Icon>
                  {site.time_zone}
                </Content>
              )}
              {site.physical_address && (
                <Content component="p">
                  <Icon size="sm" style={{ marginRight: '6px' }}>
                    <MapMarkerIcon />
                  </Icon>
                  {site.physical_address}
                </Content>
              )}
            </CardBody>
          </Card>
        </GalleryItem>
      ))}
    </Gallery>
  );
}

export default Overview;
